import { useEffect, useRef } from 'react';
import { getAccessToken } from '@/services/api';
import { BUILD_MARKER } from '@/utils/buildInfo';
import { devLog } from '@/utils/logger';
import type { FreezeDetectorContext, FreezeDetectorMessage } from '@/workers/freezeDetector.worker';

export type { FreezeDetectorContext };

/** How often the main thread heartbeats the worker. */
const PING_INTERVAL_MS = 2000;

/**
 * Mirrors the API base URL resolution in `src/services/api.ts`:
 * - `VITE_API_URL=''` (set in production .env) means relative URLs
 * - unset (dev) falls back to localhost
 *
 * Duplicated here (rather than imported) because the worker needs a fully
 * qualified URL to `fetch()` — it has no axios instance/baseURL of its own.
 */
const RAW_API_BASE = import.meta.env.VITE_API_URL;
const API_BASE_URL = RAW_API_BASE !== undefined ? RAW_API_BASE : 'http://localhost:3000';
const FREEZE_REPORT_URL = `${API_BASE_URL}/api/v2/telemetry/client-freeze`;

/**
 * useFreezeDetector
 *
 * Spins up a dedicated Web Worker (`freezeDetector.worker.ts`) that watches
 * for main-thread hard freezes independently of the main thread itself.
 *
 * Why not just `PerformanceObserver('longtask')`? That API can only report
 * a long task AFTER it completes — a genuine infinite hang (the kind a user
 * eventually force-kills the tab over) would never fire it. Because this
 * detector runs on its own thread, it can still act *while* the main thread
 * is completely frozen: the main thread sends a heartbeat 'ping' every
 * `PING_INTERVAL_MS`, and if the worker stops receiving pings for more than
 * its freeze threshold, it POSTs the freeze report itself, no main-thread
 * involvement required at the moment of reporting.
 *
 * Best-effort and fully defensive: worker creation, every postMessage, and
 * teardown are all wrapped so a failure here can never throw into React
 * render or otherwise disrupt the host component (e.g. the composer).
 */
export const useFreezeDetector = (getContext: () => FreezeDetectorContext): void => {
  // Keep the latest getContext without re-running the setup effect on every
  // render (the effect below intentionally has an empty dependency array).
  const getContextRef = useRef(getContext);
  getContextRef.current = getContext;

  useEffect(() => {
    let worker: Worker | undefined;
    let intervalId: ReturnType<typeof setInterval> | undefined;
    let handleVisibilityChange: (() => void) | undefined;

    try {
      worker = new Worker(new URL('../workers/freezeDetector.worker.ts', import.meta.url), {
        type: 'module',
      });

      // Surface unexpected worker-level errors in dev only; this must never
      // propagate into the app.
      worker.onerror = (event: ErrorEvent): void => {
        devLog.warn('[FreezeDetector] worker error (non-fatal):', event.message);
      };

      const initMessage: FreezeDetectorMessage = {
        type: 'init',
        apiUrl: FREEZE_REPORT_URL,
        buildMarker: BUILD_MARKER,
      };
      worker.postMessage(initMessage);

      const sendPing = (): void => {
        try {
          if (!worker) {
            return;
          }
          const pingMessage: FreezeDetectorMessage = {
            type: 'ping',
            at: Date.now(),
            token: getAccessToken(),
            context: getContextRef.current(),
            visible: !document.hidden,
          };
          worker.postMessage(pingMessage);
        } catch (error) {
          devLog.warn('[FreezeDetector] failed to send heartbeat (non-fatal):', error);
        }
      };

      // Send an immediate heartbeat so the worker has a valid token/context
      // right away instead of waiting a full PING_INTERVAL_MS.
      sendPing();
      intervalId = setInterval(sendPing, PING_INTERVAL_MS);

      // Belt-and-suspenders: a hidden tab's `setInterval` heartbeat gets
      // throttled by the browser (down to ~1/min), which would otherwise
      // leave the worker's last-known state stale for up to a minute before
      // the throttled ping catches up. Post an immediate ping on every
      // visibility transition so the worker's `lastPingVisible` reflects
      // reality right away, both going hidden (suppress false freezes) and
      // coming back visible (re-arm genuine freeze detection promptly).
      handleVisibilityChange = (): void => {
        sendPing();
      };
      document.addEventListener('visibilitychange', handleVisibilityChange);
    } catch (error) {
      // Some environments (locked-down browsers, certain test runners)
      // disallow Web Workers entirely — degrade to "no freeze detection"
      // rather than breaking the composer.
      devLog.warn('[FreezeDetector] worker unavailable, freeze detection disabled:', error);
      worker = undefined;
    }

    return () => {
      try {
        if (intervalId !== undefined) {
          clearInterval(intervalId);
        }
        if (handleVisibilityChange) {
          document.removeEventListener('visibilitychange', handleVisibilityChange);
        }
        if (worker) {
          const stopMessage: FreezeDetectorMessage = { type: 'stop' };
          worker.postMessage(stopMessage);
          worker.terminate();
        }
      } catch {
        // Teardown must never throw.
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- run once on mount only; getContext is read via ref
  }, []);
};
